import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  Grid,
  IconButton,
  Divider,
  Autocomplete,
  Chip
} from '@mui/material';
import {
  Close,
  Edit,
  LocationOn
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap, GeoJSON, LayersControl, LayerGroup } from 'react-leaflet';
import L, { Icon, DivIcon } from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in react-leaflet
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Component to handle map clicks for location selection
const MapClickHandler: React.FC<{ onLocationSelect: (lat: number, lng: number) => void }> = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      const { lat, lng } = e.latlng;
      onLocationSelect(lat, lng);
    },
  });
  return null;
};

// Component to recenter map when center prop changes
const RecenterMap: React.FC<{ center: [number, number] }> = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center && center[0] !== 0 && center[1] !== 0) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
};

interface Location {
  latitude: number;
  longitude: number;
}

interface Station {
  uuid: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at?: string;
  location: Location;
  elevation?: number;
  notes?: string;
  owner?: string;
  public: boolean;
  source?: string;
  timezone?: string;
  status: string;
  debug: boolean;
  latest_readings?: Record<string, any>;
  tags?: string[];
}

interface StationEditDialogProps {
  open: boolean;
  onClose: () => void;
  station?: Station;
  onSave: (stationData: Partial<Station>) => Promise<void>;
}

const StationEditDialog: React.FC<StationEditDialogProps> = ({
  open,
  onClose,
  station,
  onSave
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    latitude: '',
    longitude: '',
    elevation: '',
    notes: '',
    owner: '',
    timezone: '',
    source: '',
    public: false,
    status: 'active',
    tags: [] as string[]
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState<[number, number]>([40.7128, -74.0060]); // Default to NYC
  const [giscoBoundaries, setGiscoBoundaries] = useState<any>(null);
  const [giscoLabelPoints, setGiscoLabelPoints] = useState<any>(null);

  useEffect(() => {
    fetch('https://gisco-services.ec.europa.eu/distribution/v2/countries/geojson/CNTR_RG_20M_2024_4326.geojson')
      .then(res => res.json())
      .then(data => setGiscoBoundaries(data))
      .catch(err => console.warn('Failed to load GISCO boundaries:', err));

    fetch('https://gisco-services.ec.europa.eu/distribution/v2/countries/geojson/CNTR_LB_2024_4326.geojson')
      .then(res => res.json())
      .then(data => setGiscoLabelPoints(data))
      .catch(err => console.warn('Failed to load GISCO label points:', err));
  }, []);

  const giscoBoundaryStyle = {
    color: '#2c3e50', weight: 2, opacity: 0.8, fillColor: 'transparent', fillOpacity: 0,
  };

  const toponymLabels = React.useMemo(() => {
    if (giscoLabelPoints) {
      return giscoLabelPoints.features
        .filter((f: any) => f.properties?.NAME_ENGL && f.geometry?.coordinates)
        .map((feature: any) => ({
          name: feature.properties.NAME_ENGL,
          lat: feature.geometry.coordinates[1],
          lng: feature.geometry.coordinates[0],
        }));
    }
    return [];
  }, [giscoLabelPoints]);

  const [showGiscoBoundaries, setShowGiscoBoundaries] = useState(true);
  const [showGiscoToponyms, setShowGiscoToponyms] = useState(false);
  const [showOsm, setShowOsm] = useState(true);

  const GiscoLayerEvents = () => {
    const map = useMap();
    useEffect(() => {
      const onAdd = (e: any) => {
        if (e.name === 'GISCO Boundaries') setShowGiscoBoundaries(true);
        if (e.name === 'Official EC Toponyms') setShowGiscoToponyms(true);
        if (e.name === 'OpenStreetMap') setShowOsm(true);
      };
      const onRemove = (e: any) => {
        if (e.name === 'GISCO Boundaries') setShowGiscoBoundaries(false);
        if (e.name === 'Official EC Toponyms') setShowGiscoToponyms(false);
        if (e.name === 'OpenStreetMap') setShowOsm(false);
      };
      map.on('overlayadd', onAdd);
      map.on('overlayremove', onRemove);
      return () => { map.off('overlayadd', onAdd); map.off('overlayremove', onRemove); };
    }, [map]);
    return null;
  };

  const GiscoBaseOpacity = () => {
    const map = useMap();
    useEffect(() => {
      const giscoActive = showGiscoBoundaries || showGiscoToponyms;
      map.eachLayer((l: any) => {
        if (l.setOpacity && l._url && (l._url.includes('openstreetmap') || l._url.includes('cartocdn'))) {
          l.setOpacity(giscoActive ? 0.5 : 1);
        }
      });
    }, [showGiscoBoundaries, showGiscoToponyms, map]);
    return null;
  };

  const GiscoDisclaimer = () => {
    const map = useMap();
    useEffect(() => {
      const disclaimer = new L.Control({ position: 'bottomright' });
      disclaimer.onAdd = () => {
        const div = L.DomUtil.create('div', 'gisco-disclaimer');
        div.style.cssText = 'background:rgba(255,255,255,0.85);padding:4px 8px;font-size:10px;max-width:420px;line-height:1.3;color:#333;border-radius:3px;pointer-events:none;margin-right:10px;margin-bottom:5px;';
        div.innerHTML = 'The designations employed and the presentation of material on this map do not imply the expression of any opinion whatsoever concerning the legal status of any country, territory, city or area or of its authorities, or concerning the delimitation of its frontiers or boundaries.';
        return div;
      };
      disclaimer.addTo(map);
      return () => { disclaimer.remove(); };
    }, [map]);
    return null;
  };

  useEffect(() => {
    if (station) {
      setFormData({
        name: station.name || '',
        description: station.description || '',
        latitude: station.location?.latitude?.toString() || '',
        longitude: station.location?.longitude?.toString() || '',
        elevation: station.elevation?.toString() || '',
        notes: station.notes || '',
        owner: station.owner || '',
        timezone: station.timezone || '',
        source: station.source || '',
        public: station.public || false,
        status: station.status || 'active',
        tags: station.tags || []
      });

      // Update map center if station has coordinates
      if (station.location?.latitude && station.location?.longitude) {
        setMapCenter([station.location.latitude, station.location.longitude]);
      }
    }
  }, [station]);

  const handleMapLocationSelect = (lat: number, lng: number) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat.toString(),
      longitude: lng.toString()
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Station name is required';
    }

    if (!formData.latitude || isNaN(Number(formData.latitude))) {
      newErrors.latitude = 'Latitude is required and must be a number';
    } else {
      const lat = Number(formData.latitude);
      if (lat < -90 || lat > 90) {
        newErrors.latitude = 'Latitude must be between -90 and 90';
      }
    }

    if (!formData.longitude || isNaN(Number(formData.longitude))) {
      newErrors.longitude = 'Longitude is required and must be a number';
    } else {
      const lng = Number(formData.longitude);
      if (lng < -180 || lng > 180) {
        newErrors.longitude = 'Longitude must be between -180 and 180';
      }
    }

    if (formData.elevation && isNaN(Number(formData.elevation))) {
      newErrors.elevation = 'Elevation must be a number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const updatedData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        location: {
          latitude: Number(formData.latitude),
          longitude: Number(formData.longitude)
        },
        elevation: formData.elevation && !isNaN(Number(formData.elevation)) ? Number(formData.elevation) : undefined,
        notes: formData.notes.trim() || undefined,
        owner: formData.owner.trim() || undefined,
        timezone: formData.timezone.trim() || undefined,
        source: formData.source || undefined,
        public: formData.public,
        status: formData.status,
        tags: formData.tags
      };

      await onSave(updatedData);
      onClose();
    } catch (error: any) {
      console.error('Error updating station:', error);
      // Check if it's a unique constraint error
      if (error.response?.data?.error_type === 'unique_constraint') {
        setErrors({ submit: error.response.data.detail || 'A station with this name and source already exists. Please choose a different name or source.' });
      } else if (error.response?.data?.detail) {
        setErrors({ submit: error.response.data.detail });
      } else {
        setErrors({ submit: 'Error updating station' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  if (!station) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Edit color="primary" />
            <Typography variant="h6">
              Edit Station
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ py: 2 }}>
          {errors.submit && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.submit}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Station Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn color="primary" />
                Location
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Latitude"
                value={formData.latitude}
                onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                error={!!errors.latitude}
                helperText={errors.latitude}
                required
                type="number"
                inputProps={{ step: "any" }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Longitude"
                value={formData.longitude}
                onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                error={!!errors.longitude}
                helperText={errors.longitude}
                required
                type="number"
                inputProps={{ step: "any" }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LocationOn color="primary" />
                Location - Click on map to update position
              </Typography>
              <Box sx={{ height: '300px', borderRadius: 2, overflow: 'hidden', border: '1px solid #ddd', mb: 2 }}>
                <MapContainer
                  center={formData.latitude && formData.longitude ?
                    [Number(formData.latitude), Number(formData.longitude)] : mapCenter}
                  zoom={13}
                  style={{ height: '100%', width: '100%' }}
                  attributionControl={false}
                >
                  <GiscoLayerEvents />
                  <GiscoBaseOpacity />
                  <LayersControl position="topright">
                    <LayersControl.Overlay checked name="OpenStreetMap">
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        subdomains="abc"
                        maxZoom={19}
                      />
                    </LayersControl.Overlay>
                    <LayersControl.Overlay checked name="GISCO Boundaries">
                      <LayerGroup>
                        {giscoBoundaries && (
                          <GeoJSON data={giscoBoundaries} style={giscoBoundaryStyle} />
                        )}
                      </LayerGroup>
                    </LayersControl.Overlay>
                    <LayersControl.Overlay name="Official EC Toponyms">
                      <LayerGroup>
                        {toponymLabels.map((t: any) => (
                          <Marker key={t.name} position={[t.lat, t.lng]} interactive={false}
                            icon={new DivIcon({
                              className: 'gisco-toponym',
                              html: `<span style="font-size:14px;font-weight:700;color:#1a237e;text-shadow:-1px -1px 0 #fff,1px -1px 0 #fff,-1px 1px 0 #fff,1px 1px 0 #fff,0 -1px 0 #fff,0 1px 0 #fff,-1px 0 0 #fff,1px 0 0 #fff;white-space:nowrap;pointer-events:none;">${t.name}</span>`,
                              iconSize: [0, 0], iconAnchor: [0, 0],
                            })}
                          />
                        ))}
                      </LayerGroup>
                    </LayersControl.Overlay>
                  </LayersControl>
                  <GiscoDisclaimer />
                  <RecenterMap
                    center={formData.latitude && formData.longitude ?
                      [Number(formData.latitude), Number(formData.longitude)] : mapCenter}
                  />
                  <MapClickHandler onLocationSelect={handleMapLocationSelect} />
                  {formData.latitude && formData.longitude && (
                    <Marker
                      position={[Number(formData.latitude), Number(formData.longitude)]}
                    />
                  )}
                </MapContainer>
              </Box>
              <Box sx={{
                p: 2,
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                mb: 2
              }}>
                <Typography variant="caption" color="text.primary" sx={{ fontWeight: 'medium' }}>
                  Current coordinates: {formData.latitude && formData.longitude ?
                    `${Number(formData.latitude).toFixed(6)}, ${Number(formData.longitude).toFixed(6)}` :
                    'Not set'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Elevation (meters)"
                value={formData.elevation}
                onChange={(e) => setFormData({ ...formData, elevation: e.target.value })}
                error={!!errors.elevation}
                helperText={errors.elevation}
                type="number"
              />
            </Grid>

            <Grid item xs={6}>
              <Autocomplete
                options={['UTC', ...(Intl as any).supportedValuesOf('timeZone')]}
                value={formData.timezone || 'UTC'}
                onChange={(_, val) => setFormData({ ...formData, timezone: val || 'UTC' })}
                groupBy={(option) => option === 'UTC' ? 'Common' : option.split('/')[0]}
                renderInput={(params) => (
                  <TextField {...params} label="Timezone" placeholder="Search timezone..." helperText="Timezone for this station's timestamps" />
                )}
                size="small"
                disableClearable
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Provider"
                value={formData.owner}
                onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                placeholder="e.g., SenCore Team, University of Athens"
                helperText="Organization or team name"
                inputProps={{ maxLength: 500 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Source"
                value={formData.source}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                select
                SelectProps={{ native: true }}
                helperText="Data source protocol"
              >
                <option value="">None</option>
                <option value="HTTP">HTTP</option>
                <option value="MQTT">MQTT</option>
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                select
                SelectProps={{ native: true }}
              >
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
                <option value="debug">Debug</option>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.public}
                    onChange={(e) => setFormData({ ...formData, public: e.target.checked })}
                  />
                }
                label="Public Station"
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={formData.tags}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, tags: newValue });
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                      key={option}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tags"
                    placeholder="Add tags (press Enter to add)"
                    helperText="Type and press Enter to add tags for grouping stations"
                  />
                )}
              />
            </Grid>

            {/* Read-only fields for information */}
            <Grid item xs={12}>
              <Box sx={{ 
                p: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.300'
              }}>
                <Typography variant="subtitle2" gutterBottom>
                  Read-only fields:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>UUID:</strong> {station.uuid}
                </Typography>
                {station.source && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Source:</strong> {station.source}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  <strong>Created:</strong> {new Date(station.created_at).toLocaleDateString('en-US')}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={<Edit />}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StationEditDialog;